import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PetType } from '@spike/model';
import { fetchOrganizationServicesInfoThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { OverFullWindow, Spinner } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import useNonInitialEffect from '@versiondos/hooks';
import {
    createEmptyTrainingService,
    Service,
    ServicePricing,
    TrainingMetadata,
    StaffMember,
    MetadataDuration
} from 'model/Service';
import { AssociatedForm } from 'model/Forms';
import { useMasterData, useMarketplace, useApiClientWrapper } from 'hooks';
import { saveBulkServicesThunk } from 'actions/services/ServicesActions';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import { TrainingPetTypeStep } from './Steps/PetType/TrainingPetTypeStep';
import { TrainingDetailsStep } from './Steps/Details/TrainingDetailsStep';
import { TrainingPricingStep } from './Steps/Pricing/TrainingPricingStep';
import { TrainingStaffStep } from './Steps/Staff/TrainingStaffStep';
import styles from './TrainingForm.module.scss';

interface TrainingFormProps {
    mode: 'add' | 'edit';
    service?: Service;
    onClose: () => void;
}

interface TrainingDetailsFormData {
    name: string;
    description?: string;
    durationHours: number;
    durationMinutes: number;
    bookingType: string;
    maxPets: number;
    inHomeService: boolean;
    olbEnabled: boolean;
    color?: string;
    forms?: AssociatedForm[];
}

export const TrainingForm: React.FC<TrainingFormProps> = ({ mode, service, onClose }) => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const fetchPendingRef = useRef(false);

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [formStep, setFormStep] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingOrganizationServices, setIsLoadingOrganizationServices] = useState(true);
    const [isEditingSpecificStep, setIsEditingSpecificStep] = useState(false);
    const [showExitPrompt, setShowExitPrompt] = useState(false);

    const [trainingService, setTrainingService] = useState<Service>(() => {
        if (mode === 'edit' && service) {
            return service;
        }
        return createEmptyTrainingService(masterData, marketplace);
    });

    useEffect(() => {
        setIsLoadingOrganizationServices(true);
        fetchPendingRef.current = false;
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchingOrganizationServicesInfo) {
            fetchPendingRef.current = true;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.FetchOrganizationServicesInfoSuccess) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.Error) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }
    }, [marketplaceStatus]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.SaveSuccess:
            case ServicesStatus.SaveBulkSuccess:
                setIsSaving(false);
                setIsSuccess(true);
                break;
            case ServicesStatus.Error:
                setIsSaving(false);
                setIsSuccess(false);
                break;
        }
    }, [servicesStatus]);

    const handlePetTypeStep = (petTypes: Array<PetType>, marketplaces?: Service['marketplaces']) => {
        setTrainingService(prev => ({
            ...prev,
            petTypes,
            marketplaces: marketplaces ?? prev.marketplaces
        }));
        setFormStep(prev => prev + 1);
    };

    const handleDetailsStep = (details: TrainingDetailsFormData) => {
        // Private ignores max_pets (capacity is always 1), so the group value is kept and comes back
        // when the service is switched to Group again instead of resetting every session's capacity.
        const maxPets = details.maxPets;
        const updatedMetadata = {
            hours: details.durationHours,
            minutes: details.durationMinutes,
            booking_type: details.bookingType,
            max_pets: maxPets,
            in_home_service: details.inHomeService
        };

        setTrainingService(prev => ({
            ...prev,
            name: details.name,
            description: details.description || '',
            olbEnabled: details.olbEnabled,
            forms: details.forms || [],
            color: details.color || '',
            trainingData: {
                hours: details.durationHours,
                minutes: details.durationMinutes,
                bookingType: details.bookingType as TrainingMetadata['bookingType'],
                maxPets,
                inHomeService: details.inHomeService,
                apptsCount: prev.trainingData?.apptsCount ?? 0
            } as TrainingMetadata,
            duration: {
                metadata: JSON.stringify(updatedMetadata)
            } as MetadataDuration
        }));
        setFormStep(prev => prev + 1);
    };

    const handlePricingStep = (pricing: ServicePricing, marketplaces: Service['marketplaces']) => {
        setTrainingService(prev => ({
            ...prev,
            pricing,
            marketplaces
        }));
        setFormStep(prev => prev + 1);
    };

    const handleStaffStep = async (staff: Array<StaffMember>, marketplaces: Service['marketplaces']) => {
        try {
            setIsSaving(true);

            const finalService: Service = {
                ...trainingService,
                staff,
                marketplaces
            };

            await dispatch(saveBulkServicesThunk(finalService));
        } catch (error) {
            setIsSaving(false);
        }
    };

    const editStepHandler = (step: number) => {
        setFormStep(step);
        setIsEditingSpecificStep(true);
    };

    const getActiveStep = () => {
        if (mode === 'edit' && !isEditingSpecificStep) {
            return 4;
        }
        return formStep;
    };

    const trainingDetailsFormData: TrainingDetailsFormData = useMemo(
        () => ({
            name: trainingService.name,
            description: trainingService.description,
            durationHours: trainingService.trainingData?.hours ?? 1,
            durationMinutes: trainingService.trainingData?.minutes ?? 0,
            bookingType: trainingService.trainingData?.bookingType || 'private_session',
            maxPets: trainingService.trainingData?.maxPets || 1,
            inHomeService: trainingService.trainingData?.inHomeService || false,
            olbEnabled: trainingService.olbEnabled,
            color: trainingService.color || undefined,
            forms: trainingService.forms || []
        }),
        [
            trainingService.name,
            trainingService.description,
            trainingService.trainingData,
            trainingService.olbEnabled,
            trainingService.color,
            trainingService.forms
        ]
    );

    const title = mode === 'edit' ? `Editing ${trainingService.name}` : 'Adding a Training Service';
    const successTitle = mode === 'edit' ? 'Updated Successfully' : 'Good Job';
    const successMessage = (
        <p className={styles.successDescription}>
            <b>{trainingService.name}</b> was {mode === 'edit' ? 'updated' : 'added'} successfully.
        </p>
    );

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={title}
                onClose={onClose}
            >
                <SuccessScreen
                    title={successTitle}
                    message={successMessage}
                    buttonLabel="Go to Training Services"
                    onClick={onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo={false}
            title={title}
            onClose={() => setShowExitPrompt(true)}
            headerClassName={styles.trainingFormHeader}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={onClose}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            {isLoadingOrganizationServices ? (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            ) : (
                <div className={styles.container}>
                    <MultiStepForm
                        steps={[
                            {
                                title: 'Pet Type and Locations',
                                description: 'Select pet categories for this service',
                                component: (
                                    <TrainingPetTypeStep
                                        selectedPetTypes={trainingService.petTypes}
                                        marketplaces={trainingService.marketplaces}
                                        serviceId={trainingService.id}
                                        onNext={handlePetTypeStep}
                                    />
                                )
                            },
                            {
                                title: 'Training Service Detail',
                                component: (
                                    <TrainingDetailsStep
                                        formData={trainingDetailsFormData}
                                        serviceId={trainingService.id}
                                        onNext={handleDetailsStep}
                                    />
                                )
                            },
                            {
                                title: 'Pricing',
                                description: 'Set up pricing and taxes for this service',
                                component: (
                                    <TrainingPricingStep
                                        selectedPetTypes={trainingService.petTypes}
                                        marketplaces={trainingService.marketplaces}
                                        existingService={mode === 'edit' ? service : undefined}
                                        onNext={handlePricingStep}
                                    />
                                )
                            },
                            {
                                title: 'Staff',
                                component: (
                                    <TrainingStaffStep
                                        marketplaces={trainingService.marketplaces}
                                        organizationServicesInfo={organizationServicesInfo}
                                        onComplete={handleStaffStep}
                                        isLoading={isSaving}
                                    />
                                )
                            }
                        ]}
                        activeStep={getActiveStep()}
                        onEdit={editStepHandler}
                        flexibleNavigation={mode === 'edit'}
                    />
                </div>
            )}
        </OverFullWindow>
    );
};

export default TrainingForm;
